import { Canvas, useFrame, type Camera } from '@react-three/fiber';
import * as THREE from 'three';

import {
  DEFAULT_VIEWER_POSITION,
  hoverImageIdAtom,
  selectedLayersAtom,
  setProjectInitialPosition,
  updateViewerStateWithInitialPosition,
  userViewerSettingsAtom,
  viewerStateAtomFamily,
} from '@/atoms';
import { alignmentsAtom, alignmentWalkAtom } from '@/atoms/alignment.atom';
import { DEFAULT_ELEVATION_PROFILE_TOOL_MODE, elevationProfileToolModeAtom } from '@/atoms/elevationProfile.atom';
import { layersAtom, selectedLayerIdsAtom } from '@/atoms/layers.atom';
import { DEFAULT_PIPE_TOOL_MODE, pipeToolModeAtom } from '@/atoms/pipe.atom';
import { stationDockOpenAtom } from '@/atoms/stationVolumes.atom';
import { DEFAULT_STOCKPILE_TOOL_MODE, stockpileToolModeAtom } from '@/atoms/stockpile.atom';
import {
  cameraMapStateAtom,
  clipBoxStateAtom,
  orthoMetersPerPixelAtom,
  viewerEvents,
  type ClipBoxInteractionMode,
  type ClipBoxMode,
} from '@/atoms/viewer.atom';
import { QuickUploadDropZone, QuickUploadSharedLinkCta } from '@/components/QuickUploadDropZone';
import { groupImagesByPosition2, project, registerProjection } from '@/core';
import { execute } from '@/graphql/execute';
import { CustomBaseMap, GoogleImagesByPositionQuery } from '@/graphql/graphql';
import { useFeature } from '@/hooks/useFeature';
import { useOpenAnalysisReport } from '@/hooks/useOpenAnalysisReport';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useSavedViews } from '@/hooks/useSavedViews';
import { useViewCaptureApply } from '@/hooks/useViewCaptureApply';
import { useViewerHotkeys } from '@/hooks/useViewerHotkeys';
import { analysisColumnHasContent } from '@/lib/analysisColumn';
import { analytics, useFeatureFlagEnabled } from '@/lib/analytics';
import type { DerivedClearanceLayerMetadata } from '@/lib/clearance/types';
import { lookAtCameraCommand } from '@/lib/lookAtCamera';
import { isTauri } from '@/lib/offline/isTauri';
import { loadFeaturesByLayerOfflineAware } from '@/lib/offline/offlineFeatures';
import { loadImagesByLayerOfflineAware } from '@/lib/offline/offlineImages';
import type { SavedViewCamera } from '@/lib/savedViews';
import { resolveScalarRangeWithOverride, type ScalarRangeMetadata } from '@/lib/scalarRange';
import type { StationVolumesLayerMeta } from '@/lib/stationVolumes/types';
import { cn } from '@/lib/utils';
import Layers from '@/pages/Project/Viewer/Layers';
import { googleImagesByPositionQuery } from '@/queries/googleImage.query';
import { getProjectQuery } from '@/queries/project.query';
import {
  AccessibilitySolid,
  BoundingBoxSolid,
  BoxSolid,
  CameraSolid,
  ChartLineSolid,
  CircleSolid,
  DownloadSolid,
  EyeSlashSolid,
  GitMergeSolid,
  GridOneSolid,
  Map,
  MountainSolid,
  RulerSolid,
  UserSolid,
} from '@mynaui/icons-react';
import { PointColorType } from '@pnext/three-loader';
import { CameraControls, CameraControlsImpl, OrthographicCamera, Stats } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  FunctionComponent,
  lazy,
  PropsWithChildren,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type SetStateAction,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlignmentOverlay } from './3D/AlignmentOverlay';
import { CrestGuideTool } from './3D/CrestGuideTool';
import { CrestGuideToolbar } from './CrestGuideToolbar';
import { CrestLinePreview } from './3D/CrestLinePreview';
import AreaMeasuringTool from './3D/AreaMeasuringTool';
import { ClearanceEnvelope } from './3D/ClearanceEnvelopePreview';
import ElevationProfileTool from './3D/ElevationProfileTool';
import MeasuringTool from './3D/MeasuringTool';
import { PipeTool } from './3D/PipeTool';
import SavedElevationProfiles from './3D/SavedElevationProfiles';
import SavedMeasurements from './3D/SavedMeasurements';
import { SavedPipes } from './3D/SavedPipes';
import { SavedStockpiles } from './3D/SavedStockpiles';
import { SceneContextMenuPicker } from './3D/SceneContextMenuPicker';
import { SnapIndicator } from './3D/SnapIndicator';
import { StationBands } from './3D/StationBands';
import { StationSectionOverlay } from './3D/StationSectionOverlay';
import { StockpileTool } from './3D/StockpileTool';
import { StockpileVisualization } from './3D/StockpileVisualization';
import { SwathPreview } from './3D/SwathPreview';
import { AddLineTool } from './AddLineTool';
import { AddLineToolbar } from './AddLineToolbar';
import { AddPointToolbar } from './AddPointToolbar';
import { AddPolygonTool } from './AddPolygonTool';
import { AddPolygonToolbar } from './AddPolygonToolbar';
import { AddSurfaceTool } from './AddSurfaceTool';
import { AddSurfaceToolbar } from './AddSurfaceToolbar';
import { AlignmentWalkToolbar, stepStation } from './AlignmentWalkToolbar';
import { ClearanceChip } from './ClearanceChip';
import { ClearanceDock } from './ClearanceDock';
import ClipBoxExportDialog from './ClipBoxExportDialog';
import { ClipBoxTool } from './ClipBoxTool';
import CoordinateInfoBox from './CoordinateInfoBox';
import { DeviationColorbar, scalarBarFormat } from './DeviationColorbar';
import { DeviationVolumePanel } from './DeviationVolumePanel';
import { ElevationProfileDrawingHint } from './ElevationProfileDrawingHint';
import { ElevationProfilePanel } from './ElevationProfilePanel';
import FeatureProperties from './FeatureProperties';
import FragmentProperties from './FragmentProperties';
import FragmentSpatialTree from './FragmentSpatialTree';
import { GeoJson } from './GeoJson';
import { GLBModel } from './GLBModel';
import GoogleStreetView from './GoogleStreetView';
import { IFC } from './IFC';
import { IFC2 } from './IFC2';
import { IPRJ } from './IPRJ2';
import { LandXML } from './LandXML';
import { LandXMLLoadingOverlay } from './LandXMLLoadingOverlay';
import LandXMLProperties from './LandXMLProperties';
import LayerProperties from './LayerProperties';
import { Loader } from './Loader';
import OrthoGrid from './OrthoGrid';
import OrthoScaleBar from './OrthoScaleBar';
import { PipeDetectPanel } from './PipeDetectPanel';
import { PointCloud } from './Pointcloud';
import PortalTooltip from './PortalTooltip';
import { RainfallHeatmapLegend } from './RainfallHeatmapLegend';
import { RainfallOverflowArrows3D } from './RainfallOverflowArrows3D';
import { RainfallPlaybackBarMount } from './RainfallPlaybackBar';
import { RainfallPondFillAnimation3D } from './RainfallPondFillAnimation3D';
import { RainfallSummaryPanel } from './RainfallSummaryPanel';
import { Splat } from './Splat';
import { SplatLoadingOverlay } from './SplatLoadingOverlay';
import { StationVolumeDock } from './StationVolumeDock';
import { StockpileComputePanel } from './StockpileComputePanel';
import { StockpileToolbar } from './StockpileToolbar';
import { FeatureGate } from './subscription/FeatureGate';
import { SurfaceDifferencePanel } from './SurfaceDifferencePanel';
import { Joystick } from './Viewer/Joystick';
import { ViewerContextMenu } from './ViewerContextMenu';
import { AnalysisColumnWindow, MinimapWindow, WalkToolbarWindow } from './ViewerWindows';
import { Xgrids } from './Xgrids';
const MapView = lazy(() => import('./MapView'));

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

interface iViewerProps extends PropsWithChildren {
  projectId: string;
  hideMap?: boolean;
  showImages?: boolean;
  hideLayers?: boolean;
}

type StoredCameraState = {
  azimuth: number;
  polar: number;
  distance: number;
  zoom: number;
  position: [number, number, number];
  target: [number, number, number];
  thirdPerson?: boolean;
  ortho?: boolean;
};

const Viewer: FunctionComponent<iViewerProps> = ({
  projectId,
  children,
  hideMap = false,
  showImages = true,
  hideLayers = false,
}) => {
  const [hidePointcloud, setHidePointcloud] = useState(false);
  const [clipBoxState, setClipBoxState] = useAtom(clipBoxStateAtom(projectId));
  const clipBoxEnabled = clipBoxState.enabled;
  const clipBoxMode = clipBoxState.clipMode;
  const [viewerState, setViewerState] = useAtom(viewerStateAtomFamily(projectId));
  const showGoogleStreetView = viewerState.showGoogleStreetView;
  const setShowGoogleStreetView = useCallback(
    (value: boolean) => setViewerState((prev) => ({ ...prev, showGoogleStreetView: value })),
    [setViewerState],
  );
  const [showMap, setShowMap] = useState(true);
  const storedCameraState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(`@actegon.gis.cameraState.${projectId}`);
      return raw ? (JSON.parse(raw) as StoredCameraState) : null;
    } catch {
      return null;
    }
  }, [projectId]);
  const [orthoCamera, setOrthoCamera] = useState(storedCameraState?.ortho ?? false);
  const [thirdPersonFlying, setThirdPersonFlying] = useState(storedCameraState?.thirdPerson ?? false);
  const [showJoystick, setShowJoystick] = useState(true);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const isGoogleStreetViewEnabled = useFeatureFlagEnabled('googleStreetview');
  const { t } = useTranslation('project');
  const { t: tFg } = useTranslation('featureGate');

  useEffect(() => {
    const checkTouchDevice = () => {
      // More comprehensive touch detection for iOS Safari and other devices
      const hasTouch =
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-ignore - for older browsers
        (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0) ||
        // Check for iOS specifically
        (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream);

      setIsTouchDevice(hasTouch);
    };
    checkTouchDevice();
  }, []);

  const [params, setParams] = useSearchParams();
  const isMeasurementsEnabled = params.get('showMeasurements') === 'true';
  const isAreaMeasurementEnabled = params.get('showAreaMeasurement') === 'true';
  const [elevationProfileMode, setElevationProfileMode] = useAtom(elevationProfileToolModeAtom);

  const [positionWGS84, setPositionWGS84] = useState<THREE.Vector3 | null>(null);

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId }),
  });

  // Register the projection from the server when project data is available
  useEffect(() => {
    if (projectData?.project?.srid && projectData.project.srid !== -1) {
      registerProjection(projectData.project.srid, projectData.project.proj4String);
    }
  }, [projectData?.project?.srid, projectData?.project?.proj4String]);

  // Project-level "default 3rd person view": applies only on the first load of
  // a session — a stored camera state (the user already navigated) wins.
  // One-shot effect because project data arrives async.
  const appliedDefaultThirdPersonRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultThirdPersonRef.current || !projectData?.project) return;
    appliedDefaultThirdPersonRef.current = true;
    if (!storedCameraState && projectData.project.defaultThirdPersonView) {
      setThirdPersonFlying(true);
    }
  }, [projectData?.project, storedCameraState]);

  const { data: googleImagesData } = useQuery({
    queryKey: ['googleImagesByPosition', positionWGS84],
    queryFn: () => execute(googleImagesByPositionQuery, { lat: positionWGS84!.y, lng: positionWGS84!.x }),
    enabled: positionWGS84 !== null && showGoogleStreetView,
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    if (googleImagesData?.images.length === 0) {
      toast.error(t('viewer3d.noStreetViewNearby'));
      setShowGoogleStreetView(false);
    }
  }, [googleImagesData?.images]);

  const [googleImages, setGoogleImages] = useState<
    (GoogleImagesByPositionQuery['images'][number] & { location: { coords: [number, number, number]; srid: string } })[]
  >([]);

  const layers = useAtomValue(selectedLayersAtom(projectId));
  const allLayers = useAtomValue(layersAtom);
  const pointcloudLayers = useMemo(() => layers.filter((l) => l.type === 'POINTCLOUD'), [layers]);
  const [stockpileMode, setStockpileMode] = useAtom(stockpileToolModeAtom);
  const [pipeMode, setPipeMode] = useAtom(pipeToolModeAtom);
  // PostHog flag for the stockpile-volume tool. Coerce undefined → false so the
  // toolbar button stays hidden until the flag positively enables it. Same
  // pattern as `isGoogleStreetViewEnabled` above.
  const isStockpileAnalysisEnabled = !!useFeatureFlagEnabled('stockpileAnalysis');
  // PostHog flag for pipe detection — coerce undefined → false so the tool stays
  // hidden until the flag positively enables it (same pattern as stockpile).
  const isPipeAnalysisEnabled = !!useFeatureFlagEnabled('pipe');
  // PostHog flag for crest-line extraction — coerce undefined → false so the
  // preview mesh, draw-guide tool and its toolbar all stay unmounted until the
  // flag positively enables it (same pattern as stockpile/pipe). Orthogonal to
  // the `analysis.crestLine` subscription gate in Layers.tsx: this flag decides
  // whether the feature exists in this deployment at all, the gate decides
  // whether this org's plan can use it.
  const isCrestEnabled = !!useFeatureFlagEnabled('crest');

  // Subscription-tier feature gates.
  const clipBoxGate = useFeature('viewer.clipBox');
  const exportClipBoxGate = useFeature('viewer.exportClipBox');
  const stationVolumesGate = useFeature('analysis.stationVolumes');
  const [exportClipBoxOpen, setExportClipBoxOpen] = useState(false);

  /**
   * The station-volumes dock's "PDF report" items (rulings T10.6 / T15.6,
   * plus the layer-stack compliance report of spec §7). The handler lives in
   * `useOpenAnalysisReport` so the whole path — dock item, paywall, dialog,
   * mutation payload — can be mounted and driven by a test; see that file for
   * why that matters.
   */
  const openStationVolumesReport = useOpenAnalysisReport();

  // Layer options for the ClipBox export dialog (POINTCLOUD layers only)
  const pointCloudLayerOptions = useMemo(
    () => pointcloudLayers.map((l) => ({ id: l.id, name: l.name })),
    [pointcloudLayers],
  );
  const defaultExportLayerId = pointCloudLayerOptions[0]?.id ?? null;

  // Per-layer settings for all pointcloud layers (used to detect active deviation shading)
  const pointcloudLayerSettings = useLayerSettingsMany(pointcloudLayers.map((l) => l.id));

  // VECTOR layers from a RAINFALL_ANALYSIS run. Each one is the user-
  // visible result of one rainfall run; the panels (summary + controls)
  // mount per VECTOR layer, NOT per containing folder, so multiple runs
  // against the same source TIN each get their own panel set.
  //
  // Pairing with the TILE sibling for the raster toggle goes via
  // `metadata.rainfallRunJobId` (set by postprocess on both children).
  const rainfallVectorLayers = useMemo(
    () =>
      layers.filter((l) => {
        if (l.isHidden || l.type !== 'VECTOR') return false;
        const meta = (l.metadata ?? {}) as Record<string, unknown>;
        return meta.type === 'RAINFALL_VECTOR';
      }),
    [layers],
  );

  // RAINFALL_TILE layers that are currently *visible* — each gets a
  // small colour legend pinned next to the controls panels so the user
  // can read upstream-area values off the heatmap. Hidden ones are
  // excluded (no point showing a legend for an invisible raster).
  const visibleRainfallTileLayers = useMemo(
    () =>
      layers.filter((l) => {
        if (l.isHidden || !l.visible || l.type !== 'TILE') return false;
        const meta = (l.metadata ?? {}) as Record<string, unknown>;
        return meta.type === 'RAINFALL_TILE';
      }),
    [layers],
  );

  // Layers that are visible and actively shading by the deviation scalar
  // (source === PointColorType.CUSTOM_SCALAR)
  const deviationLayers = useMemo(
    () =>
      pointcloudLayers.filter((l) => {
        if (l.isHidden) return false;
        const meta = (l.metadata ?? {}) as Record<string, unknown>;
        if (meta.scalarAttribute !== 'Deviation') return false;
        const setting = pointcloudLayerSettings[l.id];
        return setting?.pointCloudMaterialColorSource === PointColorType.CUSTOM_SCALAR;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pointcloudLayers, pointcloudLayerSettings],
  );
  // A clearance run's derived cloud, recognised by the metadata block the
  // postprocess writes — not by `scalarAttribute`, which the user is free to
  // switch away from without the run's results ceasing to exist.
  const clearanceLayers = useMemo(
    () =>
      pointcloudLayers.filter((l) => !l.isHidden && !!((l.metadata ?? {}) as DerivedClearanceLayerMetadata).clearance),
    [pointcloudLayers],
  );
  const ifcModelLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'IFC'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layers
        .filter((l) => l.type === 'MODEL' && l.metadata?.type === 'IFC')
        .map((l) => l.id)
        .join(','),
    ],
  );
  const landxmlLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'LANDXML'),
    [layers],
  );
  const glbLayers = useMemo(() => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'GLB'), [layers]);
  // GLB layers that are on-screen AND carry surface-difference metadata —
  // these get the cut/fill results overlay in the top-right analysis stack.
  const visibleGlbLayers = useMemo(
    () => glbLayers.filter((l) => !l.isHidden && !!(l.metadata as Record<string, any> | null)?.surfaceDifference),
    [glbLayers],
  );
  const xgridsLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'XGRIDS'),
    [layers],
  );
  const splatLayers = useMemo(() => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'SPLAT'), [layers]);
  const fragmentLayers = useMemo(
    () => layers.filter((l) => l.type === 'MODEL' && l.metadata?.type === 'FRAGMENT'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      layers
        .filter((l) => l.type === 'MODEL' && l.metadata?.type === 'FRAGMENT')
        .map((l) => l.id)
        .join(','),
    ],
  );

  const { data: imagesData } = useQuery({
    queryKey: [
      'images',
      layers
        .filter((l) => l.type === 'IMAGE')
        .map((l) => l.id)
        .join(','),
    ],
    queryFn: () =>
      loadImagesByLayerOfflineAware(
        layers.filter((l) => l.type === 'IMAGE').map((l) => l.id),
        projectId,
      ),
  });

  const vectorLayers = useMemo(() => layers.filter((layer) => layer.type === 'VECTOR'), [layers]);

  const featureQueries = useQueries({
    queries: vectorLayers.map((layer) => ({
      queryKey: ['features', layer.id],
      queryFn: () => loadFeaturesByLayerOfflineAware([layer.id], projectId),
    })),
  });

  // Stable key: only recompute when query success states or data references actually change
  const featureDataKey = featureQueries.map((q) => `${q.isSuccess}:${q.dataUpdatedAt}`).join('|');
  const features = useMemo(
    () => featureQueries.flatMap((query) => query.data ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [featureDataKey],
  );

  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** `#canvas-container` — the region the movable windows live in (spec §12.1). */
  const canvasRegionRef = useRef<HTMLDivElement | null>(null);

  const _handleScreenshot = useCallback(() => {
    if (!canvasRef.current) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `screenshot-${Date.now()}.png`;
    a.click();
  }, []);

  const _handleToggleMeasurements = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('showMeasurements', isMeasurementsEnabled ? 'false' : 'true');
    if (!isMeasurementsEnabled) params.set('showAreaMeasurement', 'false');
    analytics.capture(isMeasurementsEnabled ? 'Project Measurements Disabled' : 'Project Measurements Enabled');
    setParams(params);
  };

  const _handleToggleElevationProfile = () => {
    // Toolbar toggle:
    //   closed  → start a fresh drawing session
    //   drawing → cancel the in-progress polyline
    //   panel   → close the panel (and any active draft view)
    if (elevationProfileMode.phase === 'closed') {
      setElevationProfileMode({ phase: 'drawing', activeId: null });
    } else {
      setElevationProfileMode({ ...DEFAULT_ELEVATION_PROFILE_TOOL_MODE });
    }
  };

  const _handleToggleAreaMeasurement = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('showAreaMeasurement', isAreaMeasurementEnabled ? 'false' : 'true');
    if (!isAreaMeasurementEnabled) params.set('showMeasurements', 'false');
    analytics.capture(
      isAreaMeasurementEnabled ? 'Project Area Measurement Disabled' : 'Project Area Measurement Enabled',
    );
    setParams(params);
  };

  const images = useMemo(() => imagesData ?? [], [imagesData]);

  const viewerSettings = useAtomValue(userViewerSettingsAtom);

  // Seed the viewer-state atom's position from the project's initial position
  // on first mount (atom still at the (0,0,0) default). Runs as a layout
  // effect so the atom update commits before Controls' position-sync
  // useEffect fires — otherwise Controls would lock onto (0,0,0) on the
  // first sync and the flying-mode early-return below would block recovery.
  useLayoutEffect(() => {
    if (!projectData?.project?.initialPosition) return;
    setProjectInitialPosition(projectId, projectData.project.initialPosition);
    updateViewerStateWithInitialPosition(projectId, setViewerState);
  }, [projectData?.project?.initialPosition, projectId, setViewerState]);

  // Parsed initial position passed straight into Controls so its first
  // position-sync uses the project initial position even if the atom-driven
  // `position` prop hasn't yet caught up across the DOM ↔ R3F root boundary.
  const projectInitialPos = useMemo(() => {
    const ip = projectData?.project?.initialPosition;
    if (!ip || !ip.coords) return null;
    const [x, y, z] = ip.coords;
    return { x, y, z: z ?? 0 };
  }, [projectData?.project?.initialPosition]);

  // Auto-select base map: project first, then user, then Mapbox (null).
  // Also clear stale activeBaseMapId if the referenced base map was deleted.
  useEffect(() => {
    const projectBMs = (projectData?.project?.baseMaps ?? []) as CustomBaseMap[];
    const userBMs = (viewerSettings?.customBaseMaps ?? []) as CustomBaseMap[];
    const allBaseMaps = [...projectBMs, ...userBMs];

    if (viewerState.activeBaseMapId) {
      // Validate existing selection still exists
      if (!allBaseMaps.find((bm) => bm.id === viewerState.activeBaseMapId)) {
        // Deleted — fall through to auto-select logic below
        const autoId = projectBMs[0]?.id ?? userBMs[0]?.id ?? null;
        setViewerState((prev) => ({ ...prev, activeBaseMapId: autoId }));
      }
      return;
    }

    // No active base map — auto-select: project > user > null (Mapbox)
    const autoId = projectBMs[0]?.id ?? userBMs[0]?.id ?? null;
    if (autoId) {
      setViewerState((prev) => ({ ...prev, activeBaseMapId: autoId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData?.project?.baseMaps, viewerSettings?.customBaseMaps]);
  const position = useMemo(
    () => new THREE.Vector3(viewerState.position.x, viewerState.position.y, viewerState.position.z),
    [viewerState.position.x, viewerState.position.y, viewerState.position.z],
  );

  const setPosition = useCallback(
    (value: SetStateAction<THREE.Vector3>) => {
      setViewerState((prev) => {
        const current = new THREE.Vector3(prev.position.x, prev.position.y, prev.position.z);
        const next = typeof value === 'function' ? value(current) : value;

        return {
          ...prev,
          position: {
            x: next.x ?? DEFAULT_VIEWER_POSITION.x,
            y: next.y ?? DEFAULT_VIEWER_POSITION.y,
            z: next.z ?? DEFAULT_VIEWER_POSITION.z,
          },
        };
      });
    },
    [setViewerState],
  );

  const controlRef = useRef<CameraControls | null>(null);
  const cameraControlsRef = useRef<CameraControlsImpl | null>(null);
  const skipPositionSyncRef = useRef(false);
  const cameraRef = useRef<Camera>(null);
  const { imageId, googleImageId } = viewerState;
  const setImageId = useCallback(
    (value: SetStateAction<string | null>) => {
      setViewerState((prev) => ({
        ...prev,
        imageId: typeof value === 'function' ? value(prev.imageId) : value,
      }));
    },
    [setViewerState],
  );

  const setGoogleImageId = useCallback(
    (value: SetStateAction<string | null>) => {
      setViewerState((prev) => ({
        ...prev,
        googleImageId: typeof value === 'function' ? value(prev.googleImageId) : value,
      }));
    },
    [setViewerState],
  );
  const [hoverImageId, setHoverImageId] = useAtom(hoverImageIdAtom(projectId));

  useEffect(() => {
    if (!projectData?.project.srid || projectData.project.srid === -1) return;
    (async () => {
      project([position.x, position.y, position.z], `EPSG:${projectData?.project.srid}`).then((coords) =>
        setPositionWGS84(new THREE.Vector3(...coords)),
      );
    })();
  }, [position.x, position.y, position.z, projectData?.project.srid]);

  useEffect(() => {
    if (!projectData) return;
    if (!googleImagesData?.images) return setGoogleImages([]);

    Promise.all(
      googleImagesData.images.map(async (image) => {
        return {
          ...image,
          location: {
            coords: await project(
              [image.locationWGS84.coords[0], image.locationWGS84.coords[1], image.locationWGS84.coords[2]],
              'EPSG:4326',
              `EPSG:${projectData.project.srid}`,
            ),
            srid: `EPSG:${projectData.project.srid}`,
          },
        };
      }),
    ).then((images) => setGoogleImages(images));
  }, [googleImagesData?.images]);

  const imagePositions = useMemo(() => {
    if (!images) return null;

    function getCamerasCentroid(imageList: typeof images) {
      const centroid = new THREE.Vector3();
      for (const img of imageList) {
        centroid.add(new THREE.Vector3(img.location.coords[0], img.location.coords[1], img.location.coords[2]));
      }

      centroid.divideScalar(imageList.length);

      return centroid;
    }

    const allPositions = groupImagesByPosition2(images);
    return allPositions.map((imagePosition) => ({
      ...imagePosition,
      display: viewerSettings.showAllImagePOIs
        ? true
        : new THREE.Vector3(...imagePosition.xyz).distanceTo(position) <= (viewerSettings.imagePOIMaxDistance ?? 50),
      cameraPosition: getCamerasCentroid(imagePosition.entries),
      renderPosition: getCamerasCentroid(imagePosition.entries).sub(new THREE.Vector3(0, 0, 1.5)),
    }));
  }, [images, position, viewerSettings.imagePOIMaxDistance, viewerSettings.showAllImagePOIs]);

  useEffect(() => {
    // Wait until images have loaded before deciding anything. Nulling out
    // imageId here while the query is still in flight wipes the preserved
    // selection from a previous visit, which then makes the closest-image
    // search below kick in and pick the wrong image.
    if (!imagePositions || imagePositions.length === 0) return;
    const imagePosition = imagePositions.find((p) => p.entries[0].id === imageId);

    if (imagePosition) return;

    // Find the closest image to the atom-tracked viewer position. We can't
    // use `cameraRef.current.position` here because on a Canvas remount the
    // ref is briefly null until Controls' first sync runs, and the cached
    // images query can fire this effect before that — falling back to
    // Infinity then deterministically picks `imagePositions[0]` regardless
    // of where the user actually was.
    let closestImagePosition = imagePositions[0];
    let closestDistance = Infinity;

    imagePositions.forEach((imagePosition) => {
      const distance = position.distanceTo(new THREE.Vector3(...imagePosition.xyz));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestImagePosition = imagePosition;
      }
    });

    setImageId(closestImagePosition.entries[0].id);
  }, [position, imagePositions, imageId, setImageId]);

  const isFlying = (layers.every((l) => l.type !== 'IMAGE') || !showImages) && !showGoogleStreetView;
  // const isFlying = false;

  // The 3rd-person / orthographic camera toggles only make sense in flying
  // mode (i.e. when no IMAGE layer is shown). To let the user switch into
  // flying mode straight from an image view, the camera buttons stay visible
  // even while images are on; clicking one first deselects the IMAGE layers
  // (which flips `isFlying` true) and then applies the camera change.
  const setSelectedLayerIds = useSetAtom(selectedLayerIdsAtom(projectId));
  const deselectImageLayers = useCallback(() => {
    const imageLayerIds = new Set(layers.filter((l) => l.type === 'IMAGE').map((l) => l.id));
    if (imageLayerIds.size === 0) return;
    setSelectedLayerIds((ids) => ids.filter((id) => !imageLayerIds.has(id)));
  }, [layers, setSelectedLayerIds]);

  // Saved-views capture/apply (shared with the Layer Manager's Saved Views section).
  const { applyView } = useViewCaptureApply(projectId);

  // Saved-views: a view apply (from the Layer Manager section or the deep-link
  // below) emits `applyCameraState`. Set the camera-mode flags from it here;
  // the in-Canvas Controls consumer performs the actual camera transform once
  // the projection type matches.
  useEffect(() => {
    return viewerEvents.on('applyCameraState', (cam) => {
      setOrthoCamera(cam.ortho);
      setThirdPersonFlying(cam.thirdPerson);
    });
  }, []);

  // Deep-link: fly to a saved view on project open via ?view=<id>
  const { data: savedViewsData } = useSavedViews(projectId);
  const appliedDeepLinkRef = useRef(false);

  useEffect(() => {
    if (appliedDeepLinkRef.current) return;
    const viewId = params.get('view');
    if (!viewId) return;
    if (allLayers.length === 0) return; // wait for layers so visibility filter is correct
    const view = savedViewsData?.savedViews.find((v) => v.id === viewId);
    if (!view) return;

    appliedDeepLinkRef.current = true;
    applyView(view);

    const next = new URLSearchParams(params);
    next.delete('view');
    setParams(next, { replace: true });
  }, [params, savedViewsData, allLayers.length, applyView, setParams]);

  /**
   * `[` / `]` while the station-volumes dock is open (Shift: ×10).
   *
   * `stepStation` is the walk toolbar's own pure step helper: in snap mode it
   * advances to the next alignment key point (landing on the start/end when
   * there is none further in that direction), otherwise it moves
   * `walk.step * mult` metres and clamps to `[0, length]`. It knows nothing
   * about bins — what makes a press land on the neighbouring bin is that the
   * dock seeds `walk.step` from the run's `stationIntervalM` when it opens. If
   * the user changes the step on the toolbar, or turns snapping on, these keys
   * follow that instead, which is the behaviour the toolbar's own `[` / `]`
   * had before the dock took the keys over.
   *
   * Read through the store rather than `useAtomValue`: `Viewer` renders the
   * whole R3F tree, and subscribing it to the walk cursor would re-render that
   * tree on every step (and on every camera-follow update).
   */
  const store = useStore();
  const stationDockOpen = useAtomValue(stationDockOpenAtom);
  const stepStationBin = useCallback(
    (dir: 1 | -1, mult: number) => {
      const walk = store.get(alignmentWalkAtom);
      if (!walk) return;
      const alignment = store.get(alignmentsAtom).get(walk.key);
      if (!alignment) return;
      store.set(alignmentWalkAtom, { ...walk, station: stepStation(alignment, walk, dir, mult) });
    },
    [store],
  );
  const onStationPrev = useCallback((mult: number) => stepStationBin(-1, mult), [stepStationBin]);
  const onStationNext = useCallback((mult: number) => stepStationBin(1, mult), [stepStationBin]);

  // Register viewer keyboard shortcuts
  useViewerHotkeys({
    onToggleMap: () => setShowMap((prev) => !prev),
    onTogglePointcloud: () => setHidePointcloud((prev) => !prev),
    onToggleClipBox: () => setClipBoxState((prev) => ({ ...prev, enabled: !prev.enabled })),
    onToggleOrthoCamera: () => {
      deselectImageLayers();
      setOrthoCamera((prev) => !prev);
    },
    onToggleThirdPerson: () => {
      deselectImageLayers();
      setThirdPersonFlying((prev) => !prev);
    },
    onToggleStreetView: () => setShowGoogleStreetView(!showGoogleStreetView),
    onToggleMeasureDistance: _handleToggleMeasurements,
    onToggleMeasureArea: _handleToggleAreaMeasurement,
    onScreenshot: _handleScreenshot,
    hasPointclouds: pointcloudLayers.length > 0,
    hasMeasureableTargets:
      pointcloudLayers.length > 0 || ifcModelLayers.length > 0 || fragmentLayers.length > 0 || landxmlLayers.length > 0,
    isFlying,
    canToggleCameraMode: !showGoogleStreetView,
    isGoogleStreetViewEnabled: !!isGoogleStreetViewEnabled,
    canUseClipBox: clipBoxGate.allowed,
    onStationPrev,
    onStationNext,
    // `stationDockOpenAtom` is true for the CLEARANCE dock as well (ruling R2:
    // one dock atom, one `dataset` discriminator), so these `[` / `]` hotkeys
    // are live over a clearance dock while being gated on the station-volumes
    // key. Both keys are `minProduct: 'PRO'` today so nothing is reachable that
    // should not be; split the gate here the moment that stops being true.
    hasStationVolumes: stationDockOpen,
    canUseStationVolumes: stationVolumesGate.allowed,
  });

  const googleImage = useMemo(
    () => googleImages.find((img) => img.id === googleImageId) || null,
    // hacky solution to circumvent url update in googleImage causing texture loader to load it twice
    [googleImageId, googleImages.some((img) => img.id === googleImageId)],
  );

  if (!projectData) return null;

  // The top-right analysis column is only mounted when it has something to
  // show. Nothing else keys off it any more: the minimap, the walk toolbar and
  // the column are all movable windows now (spec §12.2), each with a default
  // slot that clears the other two, so none of them has to reserve a band for
  // chrome that may or may not be on screen.
  // Through `analysisColumnHasContent` rather than an inline `||` chain: this
  // condition is the ONLY mount point for every analysis chip, and the inline
  // version silently shipped the clearance feature with no entry point at all
  // (review H1). The exact-record parameter makes the same omission a type
  // error for the next analysis.
  const analysisColumnOpen = analysisColumnHasContent({
    rainfall: rainfallVectorLayers.length,
    deviation: deviationLayers.length,
    surfaceDifference: visibleGlbLayers.length,
    clearance: clearanceLayers.length,
  });

  return (
    // The viewer is a COLUMN: one region for the canvas and all of its
    // overlays, and the station-volumes dock as a sibling below it. The dock
    // used to be an `absolute bottom-0` overlay and every bottom-anchored
    // widget (tool rail, coordinate box, joystick, gizmo, analysis column)
    // had to lift itself by the dock's height — which pushed them into the
    // middle of the canvas and into each other. As a layout row the dock
    // takes its own space: the region shrinks, R3F's ResizeObserver resizes
    // the canvas, and nothing else has to know the dock exists.
    <div className='absolute inset-0 z-10 flex flex-col overflow-hidden'>
      <div className='relative flex min-h-0 flex-1 overflow-hidden'>
        <SplatLoadingOverlay />
        <LandXMLLoadingOverlay />
        <QuickUploadDropZone projectId={projectId} />
        <div
          id='canvas-container'
          // Spec §12.1's `bounds`: the movable windows are confined to the
          // canvas region, which is the flex sibling ABOVE the station-volumes
          // dock — so a window can never be dragged over the dock.
          ref={canvasRegionRef}
          tabIndex={0}
          style={viewerSettings.backgroundColor ? { backgroundColor: viewerSettings.backgroundColor } : undefined}
          className={
            viewerSettings.backgroundColor
              ? 'overflow-hidden flex-1 min-w-0 relative'
              : 'overflow-hidden flex-1 min-w-0 relative bg-slate-100/70 dark:bg-stone-900/70'
          }>
          <Suspense fallback={null}>
            {showMap && !hideMap && (
              <MinimapWindow boundsRef={canvasRegionRef}>
                <MapView projectId={projectId} mode='overlay' boundsRef={canvasRegionRef} />
              </MinimapWindow>
            )}
          </Suspense>
          <Canvas
            // Force a fresh R3F root + Three.js scene on every project switch.
            // The DOM-level ErrorBoundary key={projectId} doesn't propagate
            // remount semantics into R3F's internal root, so without this key
            // Controls / cameraRef / storedCameraRef survive the switch and
            // restore the previous project's camera state.
            key={projectId}
            ref={canvasRef}
            // @ts-ignore
            raycaster={{ params: { Line: { threshold: 0.1 }, Points: { threshold: 0.01 } } }}
            camera={{ fov: 60, near: 0.01, far: 10_000 }}
            gl={{ preserveDrawingBuffer: true, alpha: true }}>
            <SceneBackground color={viewerSettings.backgroundColor ?? null} />
            {!isTauri() &&
            (window.location.hostname === 'localhost' || window.location.hostname === 'local.skygis.app') ? (
              <Stats />
            ) : null}
            <OrthographicCamera
              near={0.000001}
              far={10_000}
              // zoom={10}
              zoom={10}
              makeDefault={orthoCamera}
            />
            <Controls
              projectId={projectId}
              cameraRef={cameraRef}
              cameraControlsRef={cameraControlsRef}
              skipPositionSyncRef={skipPositionSyncRef}
              position={position}
              setPosition={setPosition}
              flying={isFlying}
              thirdperson={thirdPersonFlying || orthoCamera}
              isTouchDevice={isTouchDevice}
              initialAngle={projectData.project.initialAngle}
              initialPosition={projectInitialPos}
            />
            <group position={position}>
              <directionalLight position={new THREE.Vector3(10, 10, 0)} intensity={0.3} color='white' castShadow />
            </group>
            <hemisphereLight color={0xeeeeff} groundColor={0x777788} intensity={0.2} castShadow />
            <ambientLight intensity={0.3} castShadow />
            {showGoogleStreetView ? (
              <Suspense fallback={<Loader />}>
                <GoogleStreetView
                  imageId={googleImageId}
                  googleImage={googleImage}
                  setImageId={setGoogleImageId}
                  setPosition={setPosition}
                  position={position}
                  projection={`EPSG:${projectData.project.srid}`}
                  hoverImageId={hoverImageId}
                  setHoverImageId={setHoverImageId}
                  googleImages={googleImages}
                  positionWGS84={positionWGS84}
                />
              </Suspense>
            ) : null}
            {showGoogleStreetView ? null : (
              <>
                <Suspense fallback={null}>
                  <PointCloud
                    visible={!hidePointcloud}
                    layers={pointcloudLayers}
                    controlRef={controlRef}
                    project={projectData.project}
                    features={features}
                  />
                </Suspense>
                <GeoJson features={features} flying={isFlying} layers={vectorLayers} viewerPosition={position} />
                <RainfallOverflowArrows3D features={features} layers={vectorLayers} />
                <RainfallPondFillAnimation3D features={features} layers={vectorLayers} />
                {/* Shared lighting for IFC and LandXML models */}
                <directionalLight position={[1, 0.5, 2]} intensity={1.2} color='white' />
                <directionalLight position={[-0.5, -0.5, 0.5]} intensity={0.3} color={0x8899aa} />
                <ambientLight intensity={0.15} />
                <IFC layers={ifcModelLayers} />
                <IFC2 layers={fragmentLayers} />
                <LandXML layers={landxmlLayers} />
                {/* drei <Text> suspends while troika loads its font. Without a
                    boundary here the first station label — the first time the
                    dock opens — would fall through to the Canvas root and blank
                    the whole scene for a moment. */}
                <Suspense fallback={null}>
                  <AlignmentOverlay />
                </Suspense>
                <SwathPreview />
                {/* Both gated on the `crest` PostHog flag: with it off, neither
                    the saved-crest preview mesh nor the draw-guide picking tool
                    mount, so there is nothing to render or interact with even
                    if a project already has saved crest lines from before the
                    flag was disabled. */}
                {isCrestEnabled && (
                  <>
                    <CrestLinePreview />
                    <CrestGuideTool />
                  </>
                )}
                {/* The dialog's live preview while it is open (same lifecycle
                    as the swath preview: an atom the run dialog writes while
                    it is open and clears on unmount), otherwise a finished
                    run's own snapshot — see `ClearanceEnvelope`'s docstring.
                    It also carries the clearance verdict, selection and hover
                    per station bin; there is no separate clearance band mesh
                    any more, because the plates sat in the envelope's own
                    floor plane and read as its bottom. */}
                <ClearanceEnvelope />
                <StationBands />
                {/* After the bands on purpose: the section is drawn over the
                    corridor it belongs to (spec §11.2). */}
                <StationSectionOverlay />
                <GLBModel layers={glbLayers} />
                <Xgrids layers={xgridsLayers} />
                <Splat layers={splatLayers} />
                <SceneContextMenuPicker />
                <MeasuringTool projectId={projectData.project.id} features={features} />
                <AreaMeasuringTool projectId={projectData.project.id} features={features} />
                <ElevationProfileTool projectId={projectData.project.id} features={features} />
                <SavedElevationProfiles projectId={projectData.project.id} />
                <Suspense fallback={null}>
                  <SavedMeasurements projectId={projectData.project.id} />
                </Suspense>
                <ClipBoxTool projectId={projectData.project.id} />
                <AddSurfaceTool features={features} />
                <AddLineTool features={features} />
                <AddPolygonTool features={features} />
                {isPipeAnalysisEnabled && (
                  <>
                    <PipeTool projectId={projectData.project.id} />
                    <SavedPipes features={features} />
                  </>
                )}
                <SnapIndicator />
                {/* Every stockpile-related 3-D component is gated on the
                  PostHog flag. The toolbar button up top uses the same gate;
                  this pair below makes sure flag-off users also don't see
                  saved-stockpile labels or the height-column visualization
                  even if the project already contains stockpile features
                  from before the flag was disabled. */}
                {isStockpileAnalysisEnabled && (
                  <>
                    <StockpileTool />
                    <StockpileVisualization />
                    <SavedStockpiles features={features} />
                  </>
                )}
                {showImages && (
                  <IPRJ
                    setPosition={setPosition}
                    // there's a bug, imagesLayer is sometimes undefined
                    position={position}
                    images={images}
                    imagePositions={imagePositions}
                    layers={layers.filter((l) => l.type === 'IMAGE')}
                    imageId={imageId}
                    setImageId={setImageId}
                    hoverImageId={hoverImageId}
                    setHoverImageId={setHoverImageId}
                  />
                )}
              </>
            )}
          </Canvas>
          <OrthoGrid projectId={projectId} />
          {orthoCamera && <OrientationGizmo cameraRef={cameraRef} cameraControlsRef={cameraControlsRef} />}
          <AddSurfaceToolbar
            projectId={projectData.project.id}
            srid={projectData.project.srid}
            orthoCamera={orthoCamera}
            setOrthoCamera={setOrthoCamera}
            cameraControlsRef={cameraControlsRef}
            setPosition={setPosition}
            skipPositionSyncRef={skipPositionSyncRef}
          />
          <AddPointToolbar />
          <AddLineToolbar srid={projectData.project.srid} />
          {/* Crest-line "Draw centreline" sketch toolbar. A sibling of the
              Canvas, like every other FloatingToolbar — it renders plain DOM,
              which R3F cannot host. It renders itself only while draw mode is
              on, and draw mode can only be reached through the context-menu
              item in Layers.tsx (behind both the `crest` flag and the
              `analysis.crestLine` subscription gate) — so this is belt and
              braces, matching how `StockpileToolbar` below is gated even
              though it's equally unreachable with its flag off. */}
          {isCrestEnabled && <CrestGuideToolbar />}
          {/* The toolbar is a movable window now (spec §12.2). The band it used
              to be centred in — everything between the minimap's right edge and
              the analysis column's left edge — is gone with it: the window's
              default slot already clears both, and once the user has moved any
              of the three, guessing where the others are is worse than useless.
              Stacking order comes from the windows' own z band, so the old
              `z-[60]`-over-the-minimap's-`z-50` fudge is gone too. The toolbar's
              own internals are untouched; the grip is supplied by the window. */}
          <WalkToolbarWindow boundsRef={canvasRegionRef}>
            <AlignmentWalkToolbar projectId={projectData.project.id} clipBoxGate={clipBoxGate} />
          </WalkToolbarWindow>
          <AddPolygonToolbar srid={projectData.project.srid} />
          {/* Same flag gate covers the off-canvas chrome — toolbar status bar
            and the side compute panel. Without these gates, an existing
            stockpile-mode session could survive a flag flip and the user
            would still see UI for a feature they're no longer entitled to. */}
          {isStockpileAnalysisEnabled && (
            <>
              <StockpileToolbar />
              <StockpileComputePanel projectId={projectData.project.id} />
            </>
          )}
          {isPipeAnalysisEnabled && <PipeDetectPanel projectId={projectData.project.id} />}
          <ElevationProfileDrawingHint projectId={projectData.project.id} />
          <ElevationProfilePanel projectId={projectData.project.id} />
          {children}
          {/* Heatmap legends — pinned slightly left of the panels so they
            don't fight for the same column. One legend per visible
            RAINFALL_TILE layer (typical case: 0 or 1, but multi-run is
            supported). */}
          {visibleRainfallTileLayers.length > 0 && (
            <div className='absolute top-4 right-[20rem] z-20 flex flex-col items-end gap-2'>
              {visibleRainfallTileLayers.map((tile) => (
                <RainfallHeatmapLegend key={tile.id} tileLayer={tile} />
              ))}
            </div>
          )}
          {analysisColumnOpen && (
            // One movable window for ALL top-right analysis panels (rainfall
            // summaries + deviation colorbars/volume panels + surface-difference
            // panels), which is also the single flex column that keeps them from
            // stacking on top of each other. Its position, its height cap and
            // its internal scroll are `AnalysisColumnWindow`'s.
            <AnalysisColumnWindow boundsRef={canvasRegionRef}>
              {rainfallVectorLayers.map((vec) => {
                const meta = (vec.metadata ?? {}) as {
                  rainfallSummary?: any;
                  rainfallParams?: any;
                };
                if (!meta.rainfallSummary) return null;
                return (
                  <RainfallSummaryPanel
                    key={vec.id}
                    layerId={vec.id}
                    layerName={vec.name ?? ''}
                    summary={meta.rainfallSummary}
                    params={meta.rainfallParams}
                  />
                );
              })}
              {deviationLayers.map((l) => {
                const meta = (l.metadata ?? {}) as Record<string, any>;
                const setting = pointcloudLayerSettings[l.id];
                // Deliberately inline, NOT `resolveScalarRange` (finding
                // F-26): this is why the deviation colorbar is provably
                // unchanged by the clearance work (see the F-4 cross-seam
                // pass in review-final-findings.md §4). It means this
                // fallback (`-0.05`/`0.05`, per-element) and
                // `resolveScalarRange`'s own documented ±0.05 fallback for
                // malformed metadata (`lib/scalarRange.ts`) are two
                // independently maintained copies of the same default —
                // keep them in sync by hand if either changes.
                const fallbackMin = meta.deviationRange?.[0] ?? -0.05;
                const fallbackMax = meta.deviationRange?.[1] ?? 0.05;
                const min = setting?.scalarRangeMin ?? fallbackMin;
                const max = setting?.scalarRangeMax ?? fallbackMax;
                // Volume stats are opt-in (--volume-stats on the deviation
                // run); only render the panel when the layer actually carries
                // a volume sub-object on its deviationStats metadata.
                const volume = (meta.deviationStats?.volume ?? null) as Record<string, unknown> | null;
                return (
                  <div key={l.id} className='pointer-events-auto flex flex-col gap-2'>
                    <DeviationColorbar
                      layerName={l.name}
                      min={min}
                      max={max}
                      gradientName={setting?.pointCloudMaterialColor ?? 'SPECTRAL'}
                    />
                    {volume && (
                      <DeviationVolumePanel
                        projectId={projectId}
                        layerId={l.id}
                        layerName={l.name}
                        volume={volume as any}
                        // Written by the deviation postprocess when the run
                        // asked for a station breakdown (spec §10.3); the
                        // panel's chip opens the dock on this same layer.
                        stationVolumes={(meta.stationVolumes ?? null) as StationVolumesLayerMeta | null}
                      />
                    )}
                  </div>
                );
              })}
              {clearanceLayers.map((l) => {
                const meta = (l.metadata ?? {}) as DerivedClearanceLayerMetadata & ScalarRangeMetadata;
                const clearance = meta.clearance;
                if (!clearance) return null;
                const setting = pointcloudLayerSettings[l.id];
                // The colorbar is the ONLY thing on screen that says what the
                // red/blue cloud means (integrity finding I2), so it follows
                // the shading rather than the run: it shows while the cloud is
                // actually ramping over the `Clearance` scalar
                // (PointColorType.CUSTOM_SCALAR, imported here rather than
                // hardcoded — finding F-32 — unlike `postprocess.ts`'s literal
                // `51`, which is unavoidable server-side with no Potree
                // dependency) and disappears when the user switches the layer
                // back to RGB. The chip stays either way — the results outlive
                // the colouring.
                const shadingByClearance =
                  setting?.pointCloudMaterialColorSource === PointColorType.CUSTOM_SCALAR &&
                  meta.scalarAttribute === 'Clearance';
                const [min, max] = resolveScalarRangeWithOverride(meta, setting);
                // Zero is not an origin on this bar, it is the profile
                // boundary — and the cold gradation below it IS the near-miss
                // ladder the dock reports, so the ladder's inner and outer
                // bands get ticks. (Only those two: the middle band would
                // crowd them, and the reader needs the shape, not a ruler.)
                // Labels come from the bar's own formatter so a ±0.35 m range
                // shown in cm never grows a metres tick.
                const bands = clearance.params.nearMissBandsM ?? [];
                const markBands = bands.length > 1 ? [bands[0], bands[bands.length - 1]] : bands;
                const barFormat = scalarBarFormat(min, max);
                // `rampSpanM` is P98 of the intrusions (ruling P9), so the
                // worst points routinely sit PAST the end of the ramp and
                // saturate at its darkest red. The chip right below this bar
                // prints that worst value; without the `≥` the two contradict
                // each other in the same flex column.
                const worst = clearance.summary.maxIntrusionM;
                return (
                  <div key={l.id} className='pointer-events-auto flex flex-col gap-2'>
                    {shadingByClearance && (
                      <DeviationColorbar
                        layerName={l.name}
                        label={t('clearance.legend.label')}
                        zeroLabel={t('clearance.legend.boundary')}
                        marks={markBands.map((b) => ({ value: -b, label: barFormat.format(-b) }))}
                        min={min}
                        max={max}
                        maxCapped={worst != null && worst > max}
                        gradientName={setting?.pointCloudMaterialColor ?? 'CLEARANCE'}
                      />
                    )}
                    <ClearanceChip layerId={l.id} summary={clearance.summary} />
                  </div>
                );
              })}
              {visibleGlbLayers.map((l) => {
                const meta = (l.metadata ?? {}) as Record<string, any>;
                return (
                  <SurfaceDifferencePanel
                    key={l.id}
                    projectId={projectId}
                    layerId={l.id}
                    layerName={l.name}
                    meta={meta.surfaceDifference}
                  />
                );
              })}
            </AnalysisColumnWindow>
          )}
          {/* Storm-playback bar — pinned just above the bottom-right
            scale bar / coords. The component returns null when no
            STORM-mode rainfall layer is visible, so it costs nothing
            in the common case. pointer-events on the wrapper are
            disabled so the empty space around the bar doesn't eat
            canvas drags.

            Inline `right` reserves the right column for the rainfall
            results panel (w-96 = 24rem + 2rem outer margin) so the
            bar centres in the *available* viewport width and never
            renders underneath the panel. Inline rather than the
            arbitrary Tailwind value `right-[26rem]` because some JIT
            configs collapse arbitrary rem values to zero on first
            render, causing the bar to shrink to ~10 px wide; an
            inline `style` prop sidesteps that entirely. The bar
            only shows when at least one rainfall STORM layer is
            visible — at which point the panel is also visible — so
            the offset is always correct in practice. */}
          <div
            className='pointer-events-none absolute bottom-12 left-4 z-20 flex justify-center'
            style={{ right: '26rem' }}>
            <RainfallPlaybackBarMount layers={rainfallVectorLayers} />
          </div>
          <div
            data-testid='viewer-bottom-right-cluster'
            className='absolute right-2 bottom-2 z-10 flex flex-col items-end gap-1'>
            <OrthoScaleBar projectId={projectId} />
            <CoordinateInfoBox projectId={projectId} srid={projectData.project.srid} />
            {showGoogleStreetView ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${positionWGS84?.y},${positionWGS84?.x}`)}`}
                target='_blank'
                rel='noopener noreferrer'
                className='text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 opacity-70 hover:opacity-100 transition-all duration-200'>
                © {googleImage ? googleImage.copyright : 'Google Street View'}
              </a>
            ) : null}
          </div>
        </div>
        <FeatureProperties />
        <FragmentProperties />
        <FragmentSpatialTree />
        <LandXMLProperties />
        <LayerProperties />
        {!hideLayers && (
          <ErrorBoundary
            fallback={
              <div className='flex flex-col items-center justify-center px-4'>{t('viewer3d.failedLoadLayers')}</div>
            }>
            <Layers projectId={projectId} />
          </ErrorBoundary>
        )}
        {isTouchDevice && isFlying && (
          <Joystick
            visible={showJoystick}
            onMove={() => {
              // Movement is handled in the Controls component via custom events
            }}
            onEnd={() => {
              // Reset is handled in the Controls component via custom events
            }}
          />
        )}
        {clipBoxEnabled &&
          (pointcloudLayers?.length > 0 || ifcModelLayers?.length > 0 || fragmentLayers?.length > 0) && (
            <div
              className={cn(
                'absolute z-10 flex flex-col gap-1',
                viewerSettings.actionPosition === 'RIGHT' ? 'top-2 right-12 items-end' : 'bottom-12 left-2',
              )}>
              <div className='flex gap-1 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-md border border-slate-200/70 dark:border-stone-700/70 p-1'>
                {(
                  [
                    ['NONE', t('viewer3d.clipNone')],
                    ['CLIP_OUTSIDE', t('viewer3d.clipInsideOnly')],
                    ['CLIP_INSIDE', t('viewer3d.clipOutsideOnly')],
                    ['HIGHLIGHT_INSIDE', t('viewer3d.clipHighlight')],
                  ] as [ClipBoxMode, string][]
                ).map(([value, label]) => (
                  <button
                    key={value}
                    className={cn(
                      'text-xs px-2 py-1 rounded transition-colors',
                      clipBoxMode === value
                        ? 'bg-slate-800 text-white dark:bg-white dark:text-stone-700'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800',
                    )}
                    onClick={() => setClipBoxState((prev) => ({ ...prev, clipMode: value }))}>
                    {label}
                  </button>
                ))}
              </div>
              <div className='flex gap-1 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md rounded-md border border-slate-200/70 dark:border-stone-700/70 p-1'>
                {(
                  [
                    ['EDIT', t('viewer3d.clipInteractionEdit')],
                    ['VIEW', t('viewer3d.clipInteractionView')],
                  ] as [ClipBoxInteractionMode, string][]
                ).map(([value, label]) => (
                  <button
                    key={value}
                    className={cn(
                      'text-xs px-2 py-1 rounded transition-colors',
                      clipBoxState.interactionMode === value
                        ? 'bg-slate-800 text-white dark:bg-white dark:text-stone-700'
                        : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800',
                    )}
                    onClick={() => setClipBoxState((prev) => ({ ...prev, interactionMode: value }))}>
                    {label}
                  </button>
                ))}
                {pointcloudLayers.length > 0 && (
                  <>
                    <div className='self-stretch w-px bg-slate-200/80 dark:bg-stone-700/80 ml-auto mr-0.5' />
                    <button
                      className={cn(
                        'text-xs px-2 py-1 rounded transition-colors flex items-center gap-1',
                        !exportClipBoxGate.allowed
                          ? 'text-slate-400 dark:text-stone-500 cursor-pointer'
                          : 'text-slate-600 hover:bg-slate-100 dark:text-stone-300 dark:hover:bg-stone-800',
                      )}
                      onClick={() => {
                        if (!exportClipBoxGate.allowed) {
                          exportClipBoxGate.promptUpgrade();
                          return;
                        }
                        if (!clipBoxState.center) return;
                        setExportClipBoxOpen(true);
                      }}>
                      <DownloadSolid className='size-3.5' />
                      {t('clipboxExport.buttonShort')}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        {exportClipBoxGate.allowed && clipBoxEnabled && clipBoxState.center && (
          <ClipBoxExportDialog
            open={exportClipBoxOpen}
            onOpenChange={setExportClipBoxOpen}
            projectId={projectId}
            pointCloudLayers={pointCloudLayerOptions}
            defaultLayerId={defaultExportLayerId}
            clipBox={{
              center: clipBoxState.center,
              halfSize: clipBoxState.halfSize,
              rotation: clipBoxState.rotation,
            }}
          />
        )}
        <div
          id='onboarding-viewer-tools'
          // `isolate` + `transform-gpu` pin this toolbar onto its own
          // composited GPU layer with its own stacking context. Without
          // this, Chrome briefly invalidates the buttons' `backdrop-blur`
          // layer whenever a sibling in the same stacking context (e.g. a
          // layer row in the Layers panel running its `transition-colors`
          // hover animation) gets promoted to a compositing layer —
          // visible as a one-frame flicker on the toolbar buttons.
          // Reproduces in production Chrome only; dev paint timing tends
          // to hide it within the same frame.
          className={cn(
            'absolute z-10 flex gap-2 isolate transform-gpu',
            viewerSettings.actionPosition === 'RIGHT' ? 'top-2 right-2 flex-col' : 'bottom-2 left-2',
          )}>
          {!hideMap && (
            <ToggleIconButton
              active={showMap}
              icon={<Map />}
              tooltip={showMap ? t('viewer3d.hideMap') : t('viewer3d.showMap')}
              onClick={() => setShowMap(!showMap)}
              shortcutKey='M'
            />
          )}
          <ToggleIconButton
            icon={<CameraSolid />}
            tooltip={t('viewer3d.screenshot')}
            onClick={_handleScreenshot}
            shortcutKey='I'
          />
          {isGoogleStreetViewEnabled && (
            <ToggleIconButton
              active={showGoogleStreetView}
              icon={<AccessibilitySolid />}
              tooltip={showGoogleStreetView ? t('viewer3d.hideStreetView') : t('viewer3d.showStreetView')}
              onClick={() => setShowGoogleStreetView(!showGoogleStreetView)}
              shortcutKey='G'
            />
          )}
          {(pointcloudLayers?.length > 0 ||
            ifcModelLayers?.length > 0 ||
            fragmentLayers?.length > 0 ||
            landxmlLayers?.length > 0) && (
            <>
              <ToggleIconButton
                active={isMeasurementsEnabled}
                icon={<RulerSolid />}
                tooltip={isMeasurementsEnabled ? t('viewer3d.stopMeasuring') : t('viewer3d.measureDistance')}
                onClick={() => _handleToggleMeasurements()}
                shortcutKey='R'
              />
              <ToggleIconButton
                active={isAreaMeasurementEnabled}
                icon={<BoundingBoxSolid />}
                tooltip={isAreaMeasurementEnabled ? t('viewer3d.stopAreaMeasurement') : t('viewer3d.measureArea')}
                onClick={() => _handleToggleAreaMeasurement()}
                shortcutKey='B'
              />
              {pointcloudLayers?.length > 0 && (
                <FeatureGate feature='viewer.elevationProfile'>
                  <ToggleIconButton
                    active={elevationProfileMode.phase !== 'closed'}
                    icon={<ChartLineSolid />}
                    tooltip={
                      elevationProfileMode.phase === 'drawing'
                        ? t('elevationProfile.toolbarStop')
                        : t('elevationProfile.toolbarStart')
                    }
                    onClick={_handleToggleElevationProfile}
                  />
                </FeatureGate>
              )}
            </>
          )}
          {isStockpileAnalysisEnabled && pointcloudLayers?.length > 0 && (
            <FeatureGate feature='viewer.stockpile'>
              <ToggleIconButton
                active={stockpileMode.enabled}
                icon={<MountainSolid />}
                tooltip={stockpileMode.enabled ? t('viewer3d.stockpileStop') : t('viewer3d.stockpileStart')}
                onClick={() =>
                  setStockpileMode((prev) =>
                    prev.enabled ? DEFAULT_STOCKPILE_TOOL_MODE : { ...DEFAULT_STOCKPILE_TOOL_MODE, enabled: true },
                  )
                }
              />
            </FeatureGate>
          )}
          {isPipeAnalysisEnabled && pointcloudLayers?.length > 0 && (
            <FeatureGate feature='viewer.pipe'>
              <ToggleIconButton
                active={pipeMode.enabled}
                icon={<GitMergeSolid />}
                tooltip={pipeMode.enabled ? t('pipe.toolbarStop') : t('pipe.toolbarStart')}
                onClick={() =>
                  setPipeMode((prev) =>
                    prev.enabled
                      ? DEFAULT_PIPE_TOOL_MODE
                      : { ...DEFAULT_PIPE_TOOL_MODE, enabled: true, phase: 'seeking' },
                  )
                }
              />
            </FeatureGate>
          )}
          {pointcloudLayers?.length > 0 && (
            <ToggleIconButton
              active={hidePointcloud}
              icon={<EyeSlashSolid />}
              tooltip={hidePointcloud ? t('viewer3d.showPointcloud') : t('viewer3d.hidePointcloud')}
              onClick={() => setHidePointcloud(!hidePointcloud)}
              shortcutKey='P'
            />
          )}
          {(pointcloudLayers?.length > 0 || ifcModelLayers?.length > 0 || fragmentLayers?.length > 0) && (
            <ToggleIconButton
              active={clipBoxEnabled && clipBoxGate.allowed}
              locked={!clipBoxGate.allowed}
              icon={<BoxSolid />}
              tooltip={
                clipBoxGate.allowed
                  ? clipBoxEnabled
                    ? t('viewer3d.removeClipBox')
                    : t('viewer3d.clipBox')
                  : tFg('tooltip.requires', {
                      requirement:
                        clipBoxGate.gate.minProduct === 'PRO'
                          ? tFg('requirement.pro')
                          : clipBoxGate.gate.minStorageBytes != null &&
                              clipBoxGate.gate.minStorageBytes >= 1_000_000_000_000
                            ? tFg('requirement.business')
                            : clipBoxGate.gate.minStorageBytes != null
                              ? tFg('requirement.team')
                              : tFg('requirement.paid'),
                    })
              }
              onClick={
                clipBoxGate.allowed
                  ? () => setClipBoxState((prev) => ({ ...prev, enabled: !prev.enabled }))
                  : clipBoxGate.promptUpgrade
              }
              shortcutKey={clipBoxGate.allowed ? 'C' : undefined}
            />
          )}
          {!showGoogleStreetView ? (
            <>
              {isFlying && isTouchDevice && (
                <ToggleIconButton
                  active={showJoystick}
                  icon={<CircleSolid />}
                  tooltip={showJoystick ? t('viewer3d.hideJoystick') : t('viewer3d.showJoystick')}
                  onClick={() => setShowJoystick(!showJoystick)}
                />
              )}
              <ToggleIconButton
                active={thirdPersonFlying}
                disabled={orthoCamera}
                icon={<UserSolid />}
                tooltip={t('viewer3d.thirdPersonFlying')}
                onClick={() => {
                  // Drop image layers so the viewer enters flying mode, then toggle.
                  deselectImageLayers();
                  setThirdPersonFlying(!thirdPersonFlying);
                }}
                shortcutKey='T'
              />
              <ToggleIconButton
                active={orthoCamera}
                icon={<GridOneSolid />}
                tooltip={t('viewer3d.orthographicCamera')}
                onClick={() => {
                  deselectImageLayers();
                  setOrthoCamera(!orthoCamera);
                }}
                shortcutKey='O'
              />
            </>
          ) : null}
          <QuickUploadSharedLinkCta projectId={projectId} />
        </div>
        <ViewerContextMenu />
      </div>
      {/* Bottom dock, a layout sibling of the canvas region. Renders nothing
          unless a station-volumes dock is open, and owns its own height (the
          resize grip writes `stationVolumeDockAtom`, this row reads it). */}
      <StationVolumeDock projectId={projectData.project.id} onOpenPdfReport={openStationVolumesReport} />
      {/* The clearance dock shares that slot: the two read the same dock atom
          and the `dataset` discriminator decides which of them renders
          (ruling R2), so only one is ever on screen. */}
      <ClearanceDock projectId={projectData.project.id} />
    </div>
  );
};

const ToggleIconButton = ({
  icon,
  tooltip,
  onClick,
  disabled,
  locked,
  active,
  shortcutKey,
}: {
  icon: React.ReactNode;
  tooltip: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  // Visually muted but still clickable — used by feature-gate locked controls so
  // the click can open the upgrade dialog. Cannot use the HTML `disabled` attr
  // because that swallows the click event.
  locked?: boolean;
  active?: boolean;
  shortcutKey?: string;
}) => {
  const id = useId();
  return (
    <>
      <button
        data-tooltip-id={id}
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'size-8 rounded-md flex items-center justify-center [&>svg]:size-4 [&>svg]:stroke-2 border disabled:opacity-70 backdrop-blur-md',
          locked && 'opacity-60',
          active && !disabled
            ? 'bg-slate-800/80 text-white border-slate-800/70 hover:bg-slate-800 active:bg-slate-900 dark:bg-white dark:border-slate-200/70 dark:text-stone-700'
            : 'bg-white/70 border-slate-200/70 backdrop-blur-md text-slate-700 hover:bg-slate-50 active:bg-slate-100 dark:bg-stone-900/80 dark:border-stone-700/70 dark:text-stone-100',
        )}>
        {icon}
      </button>
      <PortalTooltip id={id} delayHide={0} delayShow={120}>
        {shortcutKey ? (
          <span className='flex items-center gap-2'>
            {tooltip}
            <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1.5 py-0.5 rounded text-[10px]'>
              {shortcutKey}
            </kbd>
          </span>
        ) : (
          tooltip
        )}
      </PortalTooltip>
    </>
  );
};

// CameraControls Z-up: azimuth rotates around Z, polar = angle from +Z
const GIZMO_AXES = [
  { dir: [1, 0, 0] as const, label: 'E', color: '#e74c3c', azimuth: Math.PI / 2, polar: Math.PI / 2 },
  { dir: [0, 1, 0] as const, label: 'N', color: '#2ecc71', azimuth: 0, polar: Math.PI / 2 },
  { dir: [0, 0, 1] as const, label: 'U', color: '#3498db', azimuth: 0, polar: 0.01 },
];

const OrientationGizmo = ({
  cameraRef,
  cameraControlsRef,
}: {
  cameraRef: RefObject<Camera | null>;
  cameraControlsRef: RefObject<CameraControlsImpl | null>;
}) => {
  const SIZE = 120;
  const HALF = SIZE / 2;
  const AXIS_LEN = 38;
  const [projected, setProjected] = useState(GIZMO_AXES.map((a) => ({ ...a, x: 0, y: 0, z: 0 })));
  const rafRef = useRef(0);

  useEffect(() => {
    const mat = new THREE.Matrix3();
    const v = new THREE.Vector3();

    const update = () => {
      rafRef.current = requestAnimationFrame(update);
      if (!cameraRef.current) return;
      // Extract the 3x3 rotation (inverse = transpose for rotation-only)
      mat.setFromMatrix4(cameraRef.current.matrixWorldInverse);
      const next = GIZMO_AXES.map((a) => {
        v.set(a.dir[0], a.dir[1], a.dir[2]).applyMatrix3(mat);
        return { ...a, x: v.x * AXIS_LEN, y: -v.y * AXIS_LEN, z: v.z };
      });
      setProjected(next);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafRef.current);
  }, [cameraRef]);

  const handleClick = (azimuth: number, polar: number) => {
    cameraControlsRef.current?.rotateTo(azimuth, polar, true);
  };

  // Sort by z so front axes render on top
  const sorted = [...projected].sort((a, b) => a.z - b.z);

  return (
    <div className='absolute z-20 right-2 pointer-events-auto' style={{ width: SIZE, height: SIZE, bottom: 56 }}>
      <svg width={SIZE} height={SIZE}>
        {sorted.map((a) => (
          <g key={a.label} style={{ cursor: 'pointer' }} onClick={() => handleClick(a.azimuth, a.polar)}>
            <line
              x1={HALF}
              y1={HALF}
              x2={HALF + a.x}
              y2={HALF + a.y}
              stroke={a.color}
              strokeWidth={2}
              strokeOpacity={a.z < 0 ? 0.3 : 0.9}
            />
            <circle cx={HALF + a.x} cy={HALF + a.y} r={12} fill={a.color} fillOpacity={a.z < 0 ? 0.3 : 0.9} />
            <text
              x={HALF + a.x}
              y={HALF + a.y}
              textAnchor='middle'
              dominantBaseline='central'
              fill='white'
              fontSize={10}
              fontWeight='bold'
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {a.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const SceneBackground = ({ color }: { color: string | null }) => {
  const scene = useThree((state) => state.scene);
  useEffect(() => {
    scene.background = color ? new THREE.Color(color) : null;
  }, [scene, color]);
  return null;
};

const calculateControlSpeed = (camera: Camera, canvas: HTMLCanvasElement, flying: boolean, thirdperson: boolean) => {
  if (camera instanceof THREE.OrthographicCamera) return 0.6;
  const H = canvas.clientHeight;
  const fov = camera.getEffectiveFOV();
  const vfov = THREE.MathUtils.degToRad(fov);
  const exact = (H / Math.PI) * Math.atan(Math.tan(vfov / 2) * (1 / H));
  if (flying && thirdperson) return exact * 2;
  return -exact;
};

type KeyMap = {
  KeyW: boolean;
  KeyA: boolean;
  KeyS: boolean;
  KeyD: boolean;
  KeyQ: boolean;
  KeyE: boolean;
  Space: boolean;
  ShiftLeft: boolean;
};

const useKeyboard = (): KeyMap => {
  const [keys, setKeys] = useState<KeyMap>({
    KeyW: false,
    KeyA: false,
    KeyS: false,
    KeyD: false,
    KeyQ: false,
    KeyE: false,
    Space: false,
    ShiftLeft: false,
  });

  useEffect(() => {
    const el = document.getElementById('canvas-container');
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code in keys) setKeys((k) => ({ ...k, [e.code as keyof KeyMap]: true }));
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code in keys) setKeys((k) => ({ ...k, [e.code as keyof KeyMap]: false }));
    };
    // Reset every key when the viewer/window loses focus so we never get
    // stuck "flying" because a keyup landed on a different target.
    const resetKeys = () =>
      setKeys((k) => {
        const cleared = { ...k };
        for (const code in cleared) cleared[code as keyof KeyMap] = false;
        return cleared;
      });
    // keydown is gated on the viewer element (flight only starts when focused),
    // but keyup must be global: if the cursor/focus leaves the element while a
    // key is held, the keyup fires on a different target and would otherwise
    // never reach `el`, leaving the key stuck "down".
    el.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', resetKeys);
    return () => {
      el.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', resetKeys);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return keys;
};

const Controls = ({
  projectId,
  cameraRef,
  cameraControlsRef,
  skipPositionSyncRef,
  position,
  setPosition,
  flying,
  thirdperson,
  isTouchDevice,
  initialAngle,
  initialPosition,
}: {
  projectId: string;
  cameraRef: RefObject<Camera | null>;
  cameraControlsRef: RefObject<CameraControlsImpl | null>;
  skipPositionSyncRef: RefObject<boolean>;
  position: THREE.Vector3;
  setPosition: (value: SetStateAction<THREE.Vector3>) => void;
  flying: boolean;
  thirdperson: boolean;
  isTouchDevice: boolean;
  initialAngle: number;
  initialPosition: { x: number; y: number; z: number } | null;
}) => {
  const camera = useThree((state) => state.camera);
  const canvas = useThree((state) => state.gl.domElement);
  const keys = useKeyboard();
  const hasOrientedToInitialAngle = useRef(false);
  const joystickInputRef = useRef({ x: 0, y: 0 });
  const setCameraMapState = useSetAtom(cameraMapStateAtom(projectId));
  const setOrthoMetersPerPixel = useSetAtom(orthoMetersPerPixelAtom(projectId));
  const size = useThree((s) => s.size);
  const lastCameraWriteRef = useRef(0);
  const lastRotationWriteRef = useRef(0);

  const cameraStateKey = `@actegon.gis.cameraState.${projectId}`;
  const storedCameraRef = useRef<StoredCameraState | null | undefined>(undefined);
  if (storedCameraRef.current === undefined) {
    try {
      const raw = sessionStorage.getItem(cameraStateKey);
      storedCameraRef.current = raw ? JSON.parse(raw) : null;
    } catch {
      storedCameraRef.current = null;
    }
  }

  const up = useMemo(() => new THREE.Vector3(0, 0, 1), []);
  const forward = useMemo(() => new THREE.Vector3(), []);
  const right = useMemo(() => new THREE.Vector3(), []);
  const move = useMemo(() => new THREE.Vector3(), []);

  const [rotateSpeed, setRotateSpeed] = useState(calculateControlSpeed(camera, canvas, flying, thirdperson));

  const isOrthoCamera = camera instanceof THREE.OrthographicCamera;

  // Expose joystick input handler
  useEffect(() => {
    if (!isTouchDevice) return;

    const handleJoystickMove = (event: CustomEvent<{ x: number; y: number }>) => {
      joystickInputRef.current = event.detail;
    };

    const handleJoystickEnd = () => {
      joystickInputRef.current = { x: 0, y: 0 };
    };

    window.addEventListener('joystick-move' as any, handleJoystickMove);
    window.addEventListener('joystick-end' as any, handleJoystickEnd);

    return () => {
      window.removeEventListener('joystick-move' as any, handleJoystickMove);
      window.removeEventListener('joystick-end' as any, handleJoystickEnd);
    };
  }, [isTouchDevice]);

  // Track whether we need a deferred sync (cameraControls wasn't ready when effect ran)
  const needsSyncRef = useRef(false);

  const applyPositionSync = useCallback(() => {
    if (!cameraControlsRef.current) return false;

    if (cameraRef.current && cameraRef.current !== camera && camera instanceof THREE.OrthographicCamera) {
      camera.position.copy(cameraRef.current.position);
    }

    const cameraChanged = cameraRef.current !== camera;
    cameraRef.current = camera;

    const stored = storedCameraRef.current;
    const isInitialRestore = !hasOrientedToInitialAngle.current || cameraChanged;

    if (isInitialRestore && stored) {
      hasOrientedToInitialAngle.current = true;
      // Restore full camera state from sessionStorage
      cameraControlsRef.current.setLookAt(
        stored.position[0],
        stored.position[1],
        stored.position[2],
        stored.target[0],
        stored.target[1],
        stored.target[2],
        false,
      );
      cameraControlsRef.current.rotateTo(stored.azimuth, stored.polar, false);
      cameraControlsRef.current.dollyTo(stored.distance, false);
      if (stored.zoom != null) {
        // zoomTo writes camera-controls' internal _zoom (the source of truth);
        // a direct camera.zoom assignment is overwritten by the update(0) below.
        cameraControlsRef.current.zoomTo(stored.zoom, false);
      }
      cameraControlsRef.current.update(0);
      return true;
    }

    if (!hasOrientedToInitialAngle.current) {
      hasOrientedToInitialAngle.current = true;
      // First-ever load: anchor on the project's initialPosition prop rather
      // than the atom-derived `position`. The viewer-state atom is reset on
      // mount in a layout effect on the DOM root, but R3F renders into its
      // own root, so the updated atom value may not have reached this render
      // by the time this first sync runs. Using the prop avoids a transient
      // (0,0,0) anchor that the flying-mode early-return below would lock in.
      const ix = initialPosition?.x ?? position.x;
      const iy = initialPosition?.y ?? position.y;
      const iz = initialPosition?.z ?? position.z;
      cameraControlsRef.current.rotateTo(0, Math.PI / 2, false);
      cameraControlsRef.current
        .normalizeRotations()
        .setLookAt(ix - 0.00001, iy - 0.00001, iz - 0.00001, ix, iy, iz, false);
      cameraControlsRef.current.rotateTo(-initialAngle * THREE.MathUtils.DEG2RAD, Math.PI / 2, false);
    } else {
      // Subsequent position syncs: preserve current camera rotation/distance
      const az = cameraControlsRef.current.azimuthAngle;
      const pol = cameraControlsRef.current.polarAngle;
      const dist = cameraControlsRef.current.distance;
      const zoom = camera.zoom;

      cameraControlsRef.current
        .normalizeRotations()
        .setLookAt(
          position.x - 0.00001,
          position.y - 0.00001,
          position.z - 0.00001,
          position.x,
          position.y,
          position.z,
          false,
        );

      cameraControlsRef.current.rotateTo(az, pol, false);
      cameraControlsRef.current.dollyTo(dist, false);
      camera.zoom = zoom;
      camera.updateProjectionMatrix();
    }
    // Flush CameraControls so camera.position is updated immediately.
    // Without this, components that depend on camera.position (e.g. GeoJson
    // nearbyFeatures filter) see the stale default (0,0,5) after a Canvas remount.
    cameraControlsRef.current.update(0);
    return true;
  }, [camera, cameraControlsRef, cameraRef, position, initialAngle, initialPosition]);

  useEffect(() => {
    if (skipPositionSyncRef.current) {
      skipPositionSyncRef.current = false;
      return;
    }
    if (flying && cameraRef.current === camera && !(camera instanceof THREE.OrthographicCamera)) return;

    if (!applyPositionSync()) {
      // CameraControls ref not ready yet (Canvas just remounted) — defer
      needsSyncRef.current = true;
    } else {
      needsSyncRef.current = false;
    }
  }, [cameraControlsRef, position, flying, camera, applyPositionSync]);

  // Deferred sync: when CameraControls becomes available after a Canvas remount,
  // apply the pending position sync that was missed.
  useFrame(() => {
    if (needsSyncRef.current && cameraControlsRef.current) {
      needsSyncRef.current = false;
      applyPositionSync();
    }
  });

  // Pending saved-view camera apply. Held until the live camera's projection type
  // matches the saved `ortho` flag, because applying a saved view may first flip
  // the ortho/perspective toggle (which swaps the camera object a frame later).
  const pendingCameraApplyRef = useRef<SavedViewCamera | null>(null);

  useEffect(() => {
    return viewerEvents.on('applyCameraState', (cam) => {
      pendingCameraApplyRef.current = cam;
    });
  }, []);

  useFrame(() => {
    const cam = pendingCameraApplyRef.current;
    if (!cam || !cameraControlsRef.current) return;
    const isOrtho = camera instanceof THREE.OrthographicCamera;
    if (isOrtho !== cam.ortho) return; // wait for the mode switch to land

    pendingCameraApplyRef.current = null;
    cameraControlsRef.current.setLookAt(
      cam.position[0],
      cam.position[1],
      cam.position[2],
      cam.target[0],
      cam.target[1],
      cam.target[2],
      true,
    );
    cameraControlsRef.current.rotateTo(cam.azimuth, cam.polar, true);
    cameraControlsRef.current.dollyTo(cam.distance, true);
    // Route zoom through camera-controls (zoomTo updates its internal _zoom,
    // the source of truth). Setting camera.zoom directly is clobbered back to
    // _zoom on the next per-frame controls.update() — which silently dropped
    // the saved orthographic zoom level.
    cameraControlsRef.current.zoomTo(cam.zoom, true);

    // Stop the position-sync effect from snapping the floating origin back, and
    // recenter it on the saved target.
    skipPositionSyncRef.current = true;
    setPosition(new THREE.Vector3(cam.target[0], cam.target[1], cam.target[2]));
  });

  useFrame((_, delta) => {
    if (!flying) return;

    camera.getWorldDirection(forward);
    // forward.z = 0;
    forward.normalize();

    right.crossVectors(up, forward).normalize();
    const localUp = new THREE.Vector3().crossVectors(forward, right).normalize();

    // Combine keyboard and joystick input
    const f = (keys.KeyW ? 1 : 0) - (keys.KeyS ? 1 : 0) + (isTouchDevice ? joystickInputRef.current.y : 0);
    const r = (keys.KeyA ? 1 : 0) - (keys.KeyD ? 1 : 0) + (isTouchDevice ? joystickInputRef.current.x : 0);
    const u = keys.Space || keys.KeyE ? 1 : 0;
    const d = keys.KeyQ ? 1 : 0;

    move.set(0, 0, 0);
    if (f) move.addScaledVector(forward, f);
    if (r) move.addScaledVector(right, r);
    if (u) move.addScaledVector(localUp, u);
    if (d) move.addScaledVector(localUp, -1);

    if (move.lengthSq() > 0 && cameraRef.current && cameraControlsRef.current) {
      move.normalize();
      const speed = 10;
      const v = (keys.ShiftLeft ? 4 : 1) * speed * delta;
      const newPosition = cameraRef.current.position.clone().addScaledVector(move, v);
      if (!thirdperson) {
        cameraControlsRef.current.moveTo(newPosition.x, newPosition.y, newPosition.z, false);
      } else {
        const newTarget = cameraControlsRef.current.getTarget(new THREE.Vector3()).addScaledVector(move, v);
        cameraControlsRef.current.setLookAt(
          newPosition.x,
          newPosition.y,
          newPosition.z,
          newTarget.x,
          newTarget.y,
          newTarget.z,
          false,
        );
      }
    }
  });

  // Publish camera state to atom for MapView consumption (throttled to ~50ms)
  useFrame(() => {
    const now = performance.now();
    if (now - lastCameraWriteRef.current < 50) return;
    lastCameraWriteRef.current = now;
    if (!cameraRef.current) return;

    const dir = new THREE.Vector3();
    cameraRef.current.getWorldDirection(dir);
    const rad = Math.atan2(dir.x, dir.y);
    const rotationDeg = THREE.MathUtils.radToDeg(rad) + 180;

    let fovBase = 60;
    if (cameraRef.current.type !== 'OrthographicCamera') {
      const perspCam = cameraRef.current as THREE.PerspectiveCamera;
      const halfFovRad = (perspCam.getEffectiveFOV() / 2) * (Math.PI / 180);
      fovBase = 2 * 25 * Math.tan(halfFovRad) * perspCam.aspect;
    }

    const trackPos =
      thirdperson && cameraControlsRef?.current
        ? cameraControlsRef.current.getTarget(new THREE.Vector3())
        : cameraRef.current.position;

    setCameraMapState({
      position: [trackPos.x, trackPos.y, trackPos.z],
      rotationDeg,
      fovBase,
    });

    if (cameraRef.current instanceof THREE.OrthographicCamera) {
      const o = cameraRef.current;
      const worldHeight = Math.abs(o.top - o.bottom) / Math.max(o.zoom, 1e-6);
      setOrthoMetersPerPixel(worldHeight / Math.max(size.height, 1));
    } else {
      setOrthoMetersPerPixel(null);
    }

    // Persist camera state to sessionStorage (throttled to ~500ms)
    if (cameraControlsRef?.current && cameraRef.current && now - lastRotationWriteRef.current >= 500) {
      lastRotationWriteRef.current = now;
      const t = cameraControlsRef.current.getTarget(new THREE.Vector3());
      const p = cameraRef.current.position;
      const state: StoredCameraState = {
        azimuth: cameraControlsRef.current.azimuthAngle,
        polar: cameraControlsRef.current.polarAngle,
        distance: cameraControlsRef.current.distance,
        zoom: cameraRef.current.zoom,
        position: [p.x, p.y, p.z],
        target: [t.x, t.y, t.z],
        thirdPerson: thirdperson && !isOrthoCamera,
        ortho: isOrthoCamera,
      };
      sessionStorage.setItem(cameraStateKey, JSON.stringify(state));
    }
  });

  const skipInitialDollyRef = useRef(!!storedCameraRef.current);
  useEffect(() => {
    if (!cameraControlsRef.current) return;

    if (flying && thirdperson) {
      cameraControlsRef.current.maxDistance = isOrthoCamera ? Infinity : 10000;
      if (skipInitialDollyRef.current) {
        skipInitialDollyRef.current = false;
      } else {
        cameraControlsRef.current.dollyTo(isOrthoCamera ? 1000 : 50);
      }
      cameraControlsRef.current.update(1);
    } else if (flying && !thirdperson) {
      cameraControlsRef.current.maxDistance = 0.00001;
      if (skipInitialDollyRef.current) {
        skipInitialDollyRef.current = false;
      } else {
        cameraControlsRef.current.dollyTo(0.00001);
      }
      cameraControlsRef.current.update(1);
    }
  }, [flying, thirdperson, isOrthoCamera]);

  // Jump to target (from measurements, etc.)
  useEffect(() => {
    return viewerEvents.on('jumpToTarget', (jumpTarget) => {
      if (!cameraControlsRef.current) return;
      const target = new THREE.Vector3(jumpTarget.x, jumpTarget.y, jumpTarget.z);

      if (flying && !thirdperson) {
        // First-person: place camera 2m above the target, then orient to look down at it.
        // camera-controls uses Y-up internally while the scene is Z-up, so we convert
        // the look direction into camera-controls spherical coords via the same
        // rotation matrix the position-sync effect uses.
        const offset = target.clone().add(new THREE.Vector3(0, 5, 2));
        cameraControlsRef.current.moveTo(offset.x, offset.y, offset.z, false);

        // Direction from camera (offset) toward the measurement target in world space
        const lookDir = target.clone().sub(offset).normalize();
        // camera-controls expects the *opposite* direction (from target to camera) in its Y-up frame
        const camCtrlDir = lookDir
          .clone()
          .negate()
          .applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
        const spherical = new THREE.Spherical().setFromVector3(camCtrlDir);
        cameraControlsRef.current.rotateTo(spherical.theta, spherical.phi, false);

        skipPositionSyncRef.current = true;
        setPosition(offset);
      } else {
        const currentTarget = cameraControlsRef.current.getTarget(new THREE.Vector3());
        const camDir = camera.position.clone().sub(currentTarget).normalize();
        const camPos = target.clone().add(camDir.multiplyScalar(5));
        cameraControlsRef.current.setLookAt(camPos.x, camPos.y, camPos.z, target.x, target.y, target.z, true);
        skipPositionSyncRef.current = true;
        setPosition(target.clone());
      }
    });
  }, [camera, flying, thirdperson, setPosition]);

  // "Look at section" (spec §11.3). Additive: the walk toolbar's own follow
  // emits `setCenterTarget` and keeps doing so — this only adds a pose, and
  // nothing in AlignmentWalkToolbar.tsx changes.
  //
  // The mode branch is `lookAtCameraCommand`'s (finding I8): first person pins
  // the orbit radius at ~0, so the pose has to become `moveTo` + `rotateTo`
  // there, exactly as `jumpToTarget` above does. The rule lives in
  // `lib/lookAtCamera.ts` because this file cannot be mounted in jsdom.
  useEffect(() => {
    return viewerEvents.on('lookAt', (pose) => {
      const controls = cameraControlsRef.current;
      if (!controls) return;
      const cmd = lookAtCameraCommand(pose, { flying, thirdperson });
      if (cmd.kind === 'firstPerson') {
        controls.moveTo(cmd.position[0], cmd.position[1], cmd.position[2], false);
        controls.rotateTo(cmd.theta, cmd.phi, false);
      } else {
        controls.setLookAt(
          cmd.position[0],
          cmd.position[1],
          cmd.position[2],
          cmd.target[0],
          cmd.target[1],
          cmd.target[2],
          true,
        );
      }
      // Stop the position-sync effect from snapping the floating origin back,
      // and recentre it on what we are now looking at — the same two lines the
      // saved-view apply does.
      skipPositionSyncRef.current = true;
      setPosition(new THREE.Vector3(cmd.origin[0], cmd.origin[1], cmd.origin[2]));
    });
  }, [flying, thirdperson, setPosition]);

  // Re-center orbit target (third person only)
  useEffect(() => {
    return viewerEvents.on('setCenterTarget', (target) => {
      if (!cameraControlsRef.current || !thirdperson) return;
      const t = new THREE.Vector3(target.x, target.y, target.z);
      cameraControlsRef.current.setTarget(t.x, t.y, t.z, true);
      skipPositionSyncRef.current = true;
      setPosition(t.clone());
    });
  }, [thirdperson, setPosition]);

  return (
    <CameraControls
      minDistance={flying && thirdperson && !isOrthoCamera ? 0.5 : Number.EPSILON}
      maxDistance={flying && thirdperson ? (isOrthoCamera ? Infinity : 10000) : 0.00001}
      minPolarAngle={0.017}
      maxPolarAngle={Math.PI - 0.017}
      smoothTime={0}
      draggingSmoothTime={0.03}
      azimuthRotateSpeed={rotateSpeed}
      polarRotateSpeed={rotateSpeed}
      dollySpeed={isOrthoCamera ? 1 : 0.5}
      truckSpeed={isOrthoCamera ? 1 : 6}
      enabled
      ref={cameraControlsRef}
      camera={camera}
      restThreshold={0.05}
      onRest={() => setRotateSpeed(calculateControlSpeed(camera, canvas, flying, thirdperson))}
      mouseButtons={{
        left: CameraControlsImpl.ACTION.ROTATE,
        right: flying && thirdperson ? CameraControlsImpl.ACTION.TRUCK : CameraControlsImpl.ACTION.NONE,
        middle: flying && thirdperson ? CameraControlsImpl.ACTION.DOLLY : CameraControlsImpl.ACTION.NONE,
        wheel:
          flying && thirdperson && !isOrthoCamera ? CameraControlsImpl.ACTION.DOLLY : CameraControlsImpl.ACTION.ZOOM,
      }}
      touches={{
        one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
        two:
          flying && thirdperson && !isOrthoCamera
            ? CameraControlsImpl.ACTION.TOUCH_DOLLY
            : CameraControlsImpl.ACTION.TOUCH_ZOOM,
        three:
          flying && thirdperson && !isOrthoCamera
            ? CameraControlsImpl.ACTION.TOUCH_DOLLY
            : CameraControlsImpl.ACTION.TOUCH_ZOOM,
      }}
      maxZoom={isOrthoCamera ? 500 : 8}
      minZoom={isOrthoCamera ? 1 : 0.4}
      makeDefault
    />
  );
};

Viewer.displayName = 'Viewer';

export default Viewer;
